import { useMemo } from 'react'
import { useView } from '../contexts/ViewContext'
import { useFilters } from '../contexts/FilterContext'
import { useFilteredFacilities } from '../hooks/useFilteredFacilities'
import FacilityCard from '../components/common/FacilityCard'
import EducationCard from '../components/common/EducationCard'
import Pagination from '../components/common/Pagination'
import LoadingSpinner from '../components/common/LoadingSpinner'

function GridView() {
  const { facilities, total, filtered } = useFilteredFacilities()
  const { gridState, updateGridState, showFacilityDetailModal } = useView()
  const { activeCategory } = useFilters()

  const sortedFacilities = useMemo(() => {
    if (!facilities.length) return []

    const sorted = [...facilities].sort((a, b) => {
      let aValue, bValue

      // Handle different sorting fields
      if (
        gridState.sortBy === 'name' ||
        gridState.sortBy === 'institution_name'
      ) {
        // For education, use institution_name, for health use name
        const nameField =
          activeCategory === 'education' ? 'institution_name' : 'name'
        aValue = a[nameField]?.toString().toLowerCase() || ''
        bValue = b[nameField]?.toString().toLowerCase() || ''
      } else if (
        gridState.sortBy === 'level' ||
        gridState.sortBy === 'institution_type'
      ) {
        // For education, use institution_type, for health use level
        const levelField =
          activeCategory === 'education' ? 'institution_type' : 'level'
        aValue = a[levelField]?.toString() || ''
        bValue = b[levelField]?.toString() || ''
      } else if (gridState.sortBy === 'district') {
        // Location sorting - use district field
        aValue = a.district?.toString().toLowerCase() || ''
        bValue = b.district?.toString().toLowerCase() || ''
      } else if (gridState.sortBy === 'ownership') {
        // Ownership sorting
        aValue = a.ownership?.toString().toLowerCase() || ''
        bValue = b.ownership?.toString().toLowerCase() || ''
      } else if (
        gridState.sortBy === 'details' &&
        activeCategory === 'education'
      ) {
        // Details sorting for education - facilities with data first
        const hasDetailsA = !!(
          a.total_learners ||
          a.total_teachers ||
          a.classroom_blocks
        )
        const hasDetailsB = !!(
          b.total_learners ||
          b.total_teachers ||
          b.classroom_blocks
        )

        if (hasDetailsA && !hasDetailsB)
          return gridState.sortOrder === 'asc' ? -1 : 1
        if (!hasDetailsA && hasDetailsB)
          return gridState.sortOrder === 'asc' ? 1 : -1

        // If both have details or both don't have details, sort by name as secondary
        const nameField = 'institution_name'
        aValue = a[nameField]?.toString().toLowerCase() || ''
        bValue = b[nameField]?.toString().toLowerCase() || ''
      } else {
        // Fallback to direct field access
        aValue = a[gridState.sortBy]
        bValue = b[gridState.sortBy]

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          // Already numbers, no conversion needed
        } else {
          aValue = aValue?.toString().toLowerCase() || ''
          bValue = bValue?.toString().toLowerCase() || ''
        }
      }

      if (aValue < bValue) return gridState.sortOrder === 'asc' ? -1 : 1
      if (aValue > bValue) return gridState.sortOrder === 'asc' ? 1 : -1
      return 0
    })

    return sorted
  }, [facilities, gridState.sortBy, gridState.sortOrder, activeCategory])

  const totalPages = Math.ceil(sortedFacilities.length / gridState.itemsPerPage)
  const startIndex = (gridState.currentPage - 1) * gridState.itemsPerPage
  const paginatedFacilities = sortedFacilities.slice(
    startIndex,
    startIndex + gridState.itemsPerPage
  )

  const handleSort = sortBy => {
    const newSortOrder =
      gridState.sortBy === sortBy && gridState.sortOrder === 'asc'
        ? 'desc'
        : 'asc'
    updateGridState({
      sortBy,
      sortOrder: newSortOrder,
      currentPage: 1, // Reset to first page when sorting
    })
  }

  const handlePageChange = page => {
    updateGridState({ currentPage: page })
  }

  const handleItemsPerPageChange = itemsPerPage => {
    updateGridState({
      itemsPerPage,
      currentPage: 1, // Reset to first page when changing items per page
    })
  }

  const handleFacilityClick = facility => {
    showFacilityDetailModal(facility.id)
  }

  if (!total) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <LoadingSpinner size="lg" className="mx-auto mb-4" />
          <p className="text-gray-600">Loading facilities...</p>
        </div>
      </div>
    )
  }

  if (filtered === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <div className="text-center">
          <div className="text-4xl mb-2">🔍</div>
          <p>No facilities match your current filters</p>
          <p className="text-sm mt-2">
            Try adjusting your location or filter selections
          </p>
        </div>
      </div>
    )
  }

  const getSortIcon = column => {
    if (gridState.sortBy !== column) return '↕️'
    return gridState.sortOrder === 'asc' ? '↑' : '↓'
  }

  return (
    <div className="space-y-6">
      {/* Sort Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">Sort by:</span>
          <div className="flex space-x-2">
            <button
              onClick={() =>
                handleSort(
                  activeCategory === 'education' ? 'institution_name' : 'name'
                )
              }
              className={`px-3 py-1 text-sm rounded transition-colors flex items-center space-x-1 ${
                (activeCategory === 'education' &&
                  gridState.sortBy === 'institution_name') ||
                (activeCategory === 'health' && gridState.sortBy === 'name')
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span>Name</span>
              <span className="text-xs">
                {getSortIcon(
                  activeCategory === 'education' ? 'institution_name' : 'name'
                )}
              </span>
            </button>
            <button
              onClick={() =>
                handleSort(
                  activeCategory === 'education' ? 'institution_type' : 'level'
                )
              }
              className={`px-3 py-1 text-sm rounded transition-colors flex items-center space-x-1 ${
                (activeCategory === 'education' &&
                  gridState.sortBy === 'institution_type') ||
                (activeCategory === 'health' && gridState.sortBy === 'level')
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span>{activeCategory === 'education' ? 'Type' : 'Level'}</span>
              <span className="text-xs">
                {getSortIcon(
                  activeCategory === 'education' ? 'institution_type' : 'level'
                )}
              </span>
            </button>
            <button
              onClick={() => handleSort('district')}
              className={`px-3 py-1 text-sm rounded transition-colors flex items-center space-x-1 ${
                gridState.sortBy === 'district'
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span>Location</span>
              <span className="text-xs">{getSortIcon('district')}</span>
            </button>
            {activeCategory === 'education' && (
              <>
                <button
                  onClick={() => handleSort('details')}
                  className={`px-3 py-1 text-sm rounded transition-colors flex items-center space-x-1 ${
                    gridState.sortBy === 'details'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <span>Details</span>
                  <span className="text-xs">{getSortIcon('details')}</span>
                </button>
                <button
                  onClick={() => handleSort('ownership')}
                  className={`px-3 py-1 text-sm rounded transition-colors flex items-center space-x-1 ${
                    gridState.sortBy === 'ownership'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <span>Ownership</span>
                  <span className="text-xs">{getSortIcon('ownership')}</span>
                </button>
              </>
            )}
          </div>
        </div>

        <div className="text-sm text-gray-600">
          {filtered !== total ? (
            <span>
              Showing {filtered} of {total}{' '}
              {activeCategory === 'education' ? 'institutions' : 'facilities'}
            </span>
          ) : (
            <span>
              {total}{' '}
              {activeCategory === 'education' ? 'institutions' : 'facilities'}
            </span>
          )}
        </div>
      </div>

      {/* Facility Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {paginatedFacilities.map(facility => {
          // Use appropriate card component based on category
          if (activeCategory === 'education') {
            return (
              <EducationCard
                key={facility.id}
                institution={facility}
                onClick={handleFacilityClick}
              />
            )
          }

          return (
            <FacilityCard
              key={facility.id}
              facility={facility}
              onClick={handleFacilityClick}
            />
          )
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="border-t pt-6">
          <Pagination
            currentPage={gridState.currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            itemsPerPage={gridState.itemsPerPage}
            totalItems={filtered}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>
      )}

      {/* Grid Summary */}
      <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <span className="font-medium">Page:</span> {gridState.currentPage}{' '}
            of {totalPages}
          </div>
          <div>
            <span className="font-medium">Per Page:</span>{' '}
            {gridState.itemsPerPage}
          </div>
          <div>
            <span className="font-medium">Sort:</span> {gridState.sortBy} (
            {gridState.sortOrder})
          </div>
          <div>
            <span className="font-medium">Showing:</span>{' '}
            {paginatedFacilities.length} cards
          </div>
        </div>
      </div>
    </div>
  )
}

export default GridView
